import React, { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Search as SearchIcon } from "@material-ui/icons";

const normalizar = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Tabla con búsqueda, ordenamiento, paginación y selección múltiple opcional.
 *
 * @param {{
 *  rows: Array<Object>,
 *  rowKey: (row: Object) => string | number,
 *  columns: Array<{ field: string, label: string, render?: (row) => React.ReactNode, sortValue?: (row) => any, width?: number | string, align?: string, sortable?: boolean }>,
 *  searchText?: (row: Object) => string,   Texto sobre el que se busca en cada fila
 *  searchPlaceholder?: string,
 *  selectable?: boolean,
 *  renderBulkActions?: (selectedRows: Array<Object>, clearSelection: () => void) => React.ReactNode,
 *  toolbar?: React.ReactNode,
 *  onRowClick?: (row: Object) => void,
 *  emptyMessage?: React.ReactNode,
 *  loading?: boolean,
 *  initialRowsPerPage?: number,
 * }} props
 */
const TablaSeleccionable = ({
  rows = [],
  rowKey,
  columns,
  searchText,
  searchPlaceholder = "Buscar…",
  selectable = false,
  renderBulkActions,
  toolbar,
  onRowClick,
  emptyMessage = "No hay elementos",
  loading = false,
  initialRowsPerPage = 10,
}) => {
  const classes = useStyles();
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState({ field: null, direction: "asc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [seleccion, setSeleccion] = useState(() => new Set());

  // Se descartan selecciones de filas que ya no existen (p. ej. tras eliminar)
  useEffect(() => {
    setSeleccion((prev) => {
      const keys = new Set(rows.map(rowKey));
      const next = new Set([...prev].filter((key) => keys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows, rowKey]);

  const filtradas = useMemo(() => {
    const termino = normalizar(busqueda.trim());
    let result = rows;
    if (termino && searchText) {
      const palabras = termino.split(/\s+/);
      result = rows.filter((row) => {
        const texto = normalizar(searchText(row));
        return palabras.every((palabra) => texto.includes(palabra));
      });
    }
    if (orden.field) {
      const columna = columns.find((c) => c.field === orden.field);
      const valor = columna?.sortValue ?? ((row) => row[orden.field]);
      result = [...result].sort((a, b) => {
        const va = valor(a);
        const vb = valor(b);
        const cmp =
          typeof va === "number" && typeof vb === "number"
            ? va - vb
            : String(va ?? "").localeCompare(String(vb ?? ""), "es", { numeric: true });
        return orden.direction === "asc" ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, busqueda, searchText, orden, columns]);

  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  const pagina = filtradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const seleccionadas = rows.filter((row) => seleccion.has(rowKey(row)));
  const todasFiltradasSeleccionadas =
    filtradas.length > 0 && filtradas.every((row) => seleccion.has(rowKey(row)));
  const algunaFiltradaSeleccionada = filtradas.some((row) => seleccion.has(rowKey(row)));

  const toggleFila = (key) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleTodas = () =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      filtradas.forEach((row) =>
        todasFiltradasSeleccionadas ? next.delete(rowKey(row)) : next.add(rowKey(row))
      );
      return next;
    });

  const limpiarSeleccion = () => setSeleccion(new Set());

  const ordenarPor = (field) =>
    setOrden((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));

  return (
    <div>
      <div className={classes.toolbar}>
        {searchText && (
          <TextField
            size="small"
            variant="outlined"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={searchPlaceholder}
            className={classes.search}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: "#9aa0b4" }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        {toolbar}
      </div>

      {selectable && seleccionadas.length > 0 && renderBulkActions && (
        <div className={classes.bulkBar}>
          <span>
            <strong>{seleccionadas.length}</strong> seleccionado
            {seleccionadas.length === 1 ? "" : "s"}
          </span>
          <button type="button" className={classes.linkBtn} onClick={limpiarSeleccion}>
            Quitar selección
          </button>
          <div className="ml-auto d-flex" style={{ gap: 8 }}>
            {renderBulkActions(seleccionadas, limpiarSeleccion)}
          </div>
        </div>
      )}

      <div className={classes.tableWrapper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox" className={classes.head}>
                  <Checkbox
                    size="small"
                    checked={todasFiltradasSeleccionadas}
                    indeterminate={algunaFiltradaSeleccionada && !todasFiltradasSeleccionadas}
                    onChange={toggleTodas}
                    disabled={!filtradas.length}
                    inputProps={{ "aria-label": "Seleccionar todos" }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align}
                  style={{ width: column.width }}
                  className={classes.head}
                >
                  {column.sortable === false ? (
                    column.label
                  ) : (
                    <TableSortLabel
                      active={orden.field === column.field}
                      direction={orden.field === column.field ? orden.direction : "asc"}
                      onClick={() => ordenarPor(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [0, 1, 2].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)}>
                    <Skeleton animation="wave" />
                  </TableCell>
                </TableRow>
              ))
            ) : pagina.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className={classes.empty}
                >
                  {busqueda ? "Sin resultados para la búsqueda" : emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              pagina.map((row) => {
                const key = rowKey(row);
                const selected = seleccion.has(key);
                return (
                  <TableRow
                    key={key}
                    hover
                    selected={selected}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={selected} onChange={() => toggleFila(key)} />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell key={column.field} align={column.align}>
                        {column.render ? column.render(row) : row[column.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        component="div"
        count={filtradas.length}
        page={Math.min(page, Math.max(0, Math.ceil(filtradas.length / rowsPerPage) - 1))}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </div>
  );
};

export default TablaSeleccionable;

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  search: {
    flex: "1 1 260px",
    maxWidth: 420,
    background: "#fff",
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: "8px 12px",
    marginBottom: 8,
    background: "#e6f7f1",
    border: "1px solid #bfe6da",
    borderRadius: 4,
    fontSize: "0.9em",
  },
  linkBtn: {
    border: "none",
    background: "none",
    color: "#2f8270",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
    fontSize: "0.95em",
  },
  tableWrapper: {
    maxHeight: 520,
    overflow: "auto",
    border: "1px solid #e8e8e8",
    borderRadius: 4,
    background: "#fff",
  },
  head: {
    background: "#f5f5f5",
    fontWeight: 600,
  },
  empty: {
    textAlign: "center",
    color: "#888",
    padding: "28px 0",
  },
});
